import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

const Login = () => {
    const [user, setUser] = useState("");
    const [userRecover, setUserRecover] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [recoverMode, setRecoverMode] = useState(false);
    const [buttonRecoverLabel, setButtonRecoverLabel] = useState("Demander un lien de récupération");
    const [recoverMessage, setRecoverMessage] = useState("");
    const [recoverError, setRecoverError] = useState("");
    const [isRecovering, setIsRecovering] = useState(false);

    useEffect(() => {
        document.title = "Connexion";
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError("");

        const cleanedUser = user.trim();
        try {
            const response = await axios.post(`${API_BASE_URL}/login`, {
                user: cleanedUser,
                password,
            });

            const status = response?.data?.status;

            if (status === "ok") {
                // Fetch user infos
                const infosRes = await axios.post(`${API_BASE_URL}/get_user_infos`, {
                    user: cleanedUser,
                });

                const info = Array.isArray(infosRes?.data) ? infosRes.data[0] : infosRes?.data;

                if (!info) {
                    setLoginError("Impossible de récupérer vos informations. Veuillez réessayer.");
                    return;
                }

                // Map role without mutating server response
                const roleMap = { "1": "Administrateur" };
                const mappedRole = roleMap[info.role] || info.role;

                // Store in localStorage
                localStorage.setItem("token", response.data.token || "");
                localStorage.setItem("user_id", String(info.id ?? ""));
                localStorage.setItem("user_mail", info.email ?? "");
                localStorage.setItem("user_role", mappedRole ?? "");
                localStorage.setItem("color", info.color ?? "");
                localStorage.setItem(
                    "user_name",
                    cleanedUser ? cleanedUser.charAt(0).toUpperCase() + cleanedUser.slice(1) : ""
                );

                window.location.replace("/");
            } else if (status === "wrong-credentials") {
                setLoginError("Mot de passe ou nom d'utilisateur incorrect.");
            } else if (status === "user-not-found") {
                setLoginError("Cet utilisateur n'existe pas.");
            } else if (status === "user-locked") {
                setLoginError(
                    "<p><strong>Votre compte a été verrouillé.</strong></p><p>Merci de contacter un administrateur Solution Digitale pour rétablir vos accès.</p>"
                );
            } else if (status === "account-locked") {
                setLoginError(response?.data?.message || "Votre compte est verrouillé.");
            } else {
                setLoginError("Une erreur avec le serveur Node.js s'est produite.");
            }
        } catch (err) {
            console.error("Login error:", err);
            setLoginError("Une erreur est survenue.");
        }
    };

    const handleRecover = async (e) => {
        e.preventDefault();
        setRecoverMessage("");
        setRecoverError("");

        const email = userRecover.trim().toLowerCase();

        // Basic email + domain check
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!isValidEmail || !email.endsWith("@solution-digitale.ch")) {
            setRecoverError("Veuillez entrer une adresse @solution-digitale.ch valide.");
            return;
        }

        try {
            setIsRecovering(true);

            const idRes = await axios.post(`${API_BASE_URL}/get_user_id`, { user: email });
            const userId = idRes?.data?.id;

            if (!userId) {
                setRecoverError("Utilisateur introuvable pour cette adresse.");
                return;
            }

            await axios.post(`${API_BASE_URL}/reset_user_password`, {
                user_id: userId,
                user_email: email,
            });

            // Show a ✓ then revert after 3 seconds
            setButtonRecoverLabel('<i class="fas fa-check mr-10"></i>');
            setRecoverMessage(
                `Un lien vous permettant de réinitialiser votre mot de passe vous a été envoyé à l'adresse ${email}.`
            );
            setTimeout(() => setButtonRecoverLabel("Demander un lien de récupération"), 3000);
        } catch (err) {
            console.error("Erreur reset password:", err);
            setRecoverError("Impossible d'envoyer le lien de réinitialisation. Réessayez plus tard.");
        } finally {
            setIsRecovering(false);
        }
    };

    return (
        <div>
            <div className="login-container">
                {!recoverMode ? (
                    <form className="login-form" onSubmit={handleLogin} noValidate>
                        <h2 className="title">Connectez-vous 😊</h2>

                        <div className="input-container flex-direction-column-1200">
                            <div className="input-icon">
                                <i className="fas fa-user icon" />
                            </div>
                            <input
                                className="input"
                                type="text"
                                placeholder="Utilisateur ou email@solution-digitale.ch"
                                required
                                autoComplete="username"
                                value={user}
                                onChange={(e) => setUser(e.target.value)}
                            />
                        </div>

                        <div className="input-container flex-direction-column-1200">
                            <div className="input-icon">
                                <i className="fas fa-lock icon" />
                            </div>
                            <input
                                className="input"
                                type="password"
                                placeholder="Mot de passe"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        {loginError && (
                            <p className="error-message" dangerouslySetInnerHTML={{ __html: loginError }} />
                        )}

                        <button className="btn-login" type="submit">
                            Login
                        </button>

                        <div className="display-flex mt-20">
              <span
                  onClick={() => setRecoverMode(true)}
                  className="text-left text-underline cursor-pointer color-third"
                  style={{ fontWeight: 800 }}
              >
                Mot de passe oublié ?
              </span>
                        </div>
                    </form>
                ) : (
                    <form className="login-form" onSubmit={handleRecover} noValidate>
                        <h2 className="title">Réinitialisez votre mot de passe 😊</h2>

                        <div className="input-container flex-direction-column-1200 mb-10">
                            <div className="input-icon">
                                <i className="fas fa-envelope icon" />
                            </div>
                            <input
                                className="input"
                                type="email"
                                placeholder="email@solution-digitale.ch"
                                required
                                autoComplete="email"
                                value={userRecover}
                                onChange={(e) => setUserRecover(e.target.value)}
                            />
                        </div>

                        <button
                            className="btn-login mb-20 mt-0"
                            type="submit"
                            disabled={isRecovering}
                            dangerouslySetInnerHTML={{ __html: buttonRecoverLabel }}
                        />

                        {recoverMessage && <span>{recoverMessage}</span>}
                        {recoverError && <p className="error-message">{recoverError}</p>}

                        <div className="display-flex mt-20">
              <span
                  onClick={() => {
                      setRecoverMode(false);
                      setRecoverMessage("");
                      setRecoverError("");
                  }}
                  className="text-left text-underline cursor-pointer color-third"
                  style={{ fontWeight: 800 }}
              >
                <i className="fa-regular fa-arrow-left mr-10" />
                Se connecter
              </span>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
